import type { ApiEnvelope } from '@/api/errors';
import { filenameFromContentDisposition, triggerBlobDownload } from '@/lib/download';
import type {
  AbsentDocuments,
  BargainDocTypeItem,
  BargainStepsInfo,
  ConsigneeDetails,
  DocumentTreeResponse,
  GetMyBargainsResponse,
  MyBargainsParams,
  YearSchedule,
} from '@/types/deals';
import { BaseService } from './BaseService';

// Write-операции order-service на dev дольше дефолтных 15с (Camunda/нотификации) — даём 60с.
const WRITE_TIMEOUT_MS = 60_000;

// Сервис order-service для раздела «Сделки». Конверт { success, data }.
class DealService extends BaseService {
  /** Мои сделки по типу (SALE=продажи / PURCHASE=покупки). page — 1-based (page=0 → 500 на бэке). */
  async getMyBargains(params: MyBargainsParams, signal?: AbortSignal): Promise<GetMyBargainsResponse> {
    const response = await this.request<ApiEnvelope<GetMyBargainsResponse>>(
      {
        method: 'get',
        url: '/order/bargain/my/list',
        params: {
          // Продавец/покупатель шлёт сторону, агент — без стороны; фильтр статуса (completed) — любая роль. Пустые не отправляем.
          ...(params.orderType ? { orderType: params.orderType } : {}),
          ...(params.completed !== undefined ? { completed: params.completed } : {}),
          page: params.page,
          limit: params.limit,
          ...(params.filter ? { filter: params.filter } : {}),
        },
      },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Полное состояние сделки для карточки/степпера (роль, шаги, стороны, документы, оплата). */
  async getBargainSteps(bargainId: number, signal?: AbortSignal): Promise<BargainStepsInfo> {
    const response = await this.request<ApiEnvelope<BargainStepsInfo>>(
      { method: 'get', url: `/order/bargain/${bargainId}/steps` },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Дерево документов сделки (группы → даты → ТС → документы, с подписями по ролям). */
  async getDocumentsTree(bargainId: number, signal?: AbortSignal): Promise<DocumentTreeResponse> {
    const response = await this.request<ApiEnvelope<DocumentTreeResponse>>(
      { method: 'get', url: `/order/bargain/${bargainId}/documents/tree`, params: { groupMainDocs: true } },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Скачать документ сделки (blob → файл; имя — из Content-Disposition, как в Аттестации/заявках). */
  async downloadDocument(documentId: number): Promise<void> {
    const response = await this.request<Blob>(
      { method: 'get', url: '/order/bargain/document', params: { documentId }, responseType: 'blob' },
      { idempotent: true },
    );
    const filename =
      filenameFromContentDisposition(response.headers['content-disposition'] as string | undefined) ??
      'document';
    triggerBlobDownload(response.data, filename);
  }

  // ── Шаг «Аттестация» ────────────────────────────────────────────────────────
  /** Документы, которых не хватает у контрагента (userId — проверяемый, напр. продавец). */
  async getAbsentDocuments(userId: number, bargainId: number, signal?: AbortSignal): Promise<AbsentDocuments> {
    const response = await this.request<ApiEnvelope<AbsentDocuments>>(
      { method: 'get', url: `/order/bargain/${userId}/${bargainId}/absent-documents` },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Скачать пакет документов аттестации (.zip). isSeller=true — пакет продавца. */
  async downloadVerificationZip(bargainId: number, isSeller: boolean): Promise<void> {
    const response = await this.request<Blob>(
      { method: 'get', url: '/order/bargain/verification/download/zip', params: { isSeller, bargainId }, responseType: 'blob' },
      { idempotent: true, timeoutMs: WRITE_TIMEOUT_MS },
    );
    const filename =
      filenameFromContentDisposition(response.headers['content-disposition'] as string | undefined) ??
      `attestation-${bargainId}.zip`;
    triggerBlobDownload(response.data, filename);
  }

  /** Аттестовать продавца (покупатель) → сделка идёт дальше. */
  async attest(bargainId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/attest` },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Не аттестовать продавца (покупатель) → сделка отменяется (необратимо). */
  async notAttest(bargainId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/not_attest` },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Шаг «Согласование дат отгрузки» ──────────────────────────────────────────
  /** Предложить график (продавец). */
  async suggestSellerSchedule(bargainId: number, dates: YearSchedule[]): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/shipment_schedule/seller/suggest`, data: { dates } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Предложить свой график (покупатель). */
  async suggestBuyerSchedule(bargainId: number, dates: YearSchedule[]): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/shipment_schedule/buyer/suggest`, data: { dates } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Подтвердить предложенный график. */
  async confirmSchedule(bargainId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/shipment_schedule/confirm`, data: { ticked: true } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Типы документов, которые ТЕКУЩИЙ пользователь может загрузить в ТЕКУЩЕМ состоянии сделки
   *  (бэк учитывает роль + шаг). Пусто — загрузка сейчас недоступна этому пользователю. */
  async getAvailableDocTypes(bargainId: number, signal?: AbortSignal): Promise<BargainDocTypeItem[]> {
    const response = await this.request<ApiEnvelope<{ items?: BargainDocTypeItem[] }>>(
      { method: 'get', url: `/order/bargain/${bargainId}/documents/available_types` },
      { idempotent: true, signal },
    );
    return response.data?.data?.items ?? [];
  }

  // ── Шаг «Окончательный расчёт» ───────────────────────────────────────────────
  private async confirmAction(bargainId: number, path: string): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/${path}`, data: { ticked: true } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Покупатель подтверждает оплату счёта на доплату (недоплата). */
  payUnderpayment(bargainId: number): Promise<void> {
    return this.confirmAction(bargainId, 'buyer/underpayment/paid');
  }

  /** Продавец подтверждает получение доплаты (недоплата). */
  confirmUnderpaymentReceived(bargainId: number): Promise<void> {
    return this.confirmAction(bargainId, 'seller/underpayment/confirm_paid');
  }

  /** Продавец подтверждает возврат переплаты. */
  returnOverpayment(bargainId: number): Promise<void> {
    return this.confirmAction(bargainId, 'seller/overpayment/paid');
  }

  /** Покупатель подтверждает получение возврата (переплата). */
  confirmOverpaymentReceived(bargainId: number): Promise<void> {
    return this.confirmAction(bargainId, 'buyer/overpayment/confirm_paid');
  }

  // ── Шаг «Перевозка» ──────────────────────────────────────────────────────────
  /** Покупатель подтверждает получение товара. */
  async confirmDelivery(bargainId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/buyer/confirm_delivery`, data: { ticked: true } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Шаг «Выбор грузополучателя» ──────────────────────────────────────────────
  /** Грузополучатель сделки по умолчанию (бэк определяет по базису: терминал/покупатель). */
  async getConsignee(bargainId: number, signal?: AbortSignal): Promise<ConsigneeDetails> {
    const response = await this.request<ApiEnvelope<ConsigneeDetails>>(
      { method: 'get', url: `/order/bargain/${bargainId}/consignee` },
      { idempotent: true, signal },
    );
    return response.data?.data ?? {};
  }

  /** Назначить грузополучателя (по ИНН) — запускает экспедирование. */
  async assignConsignee(bargainId: number, consigneeInn: string): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/consignee`, data: { consigneeInn } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Шаг «Подтверждение отгрузки» ─────────────────────────────────────────────
  /** Продавец подтверждает готовность товара к отгрузке (без тела). */
  async confirmShipmentReady(bargainId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/shipment_ready/confirm` },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Загрузка подписанных документов (фолбэк/симуляция при недоступном ЭДО Диадок) ──────────────
  /** Загрузить подписанный вне ЭДО документ заданного типа — продвигает подпись текущей роли. */
  async uploadSignedDocument(bargainId: number, documentType: string, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('documentType', documentType);
    formData.append('files', file, file.name);
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: `/order/bargain/${bargainId}/upload/document`, data: formData },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Шаг «Подпись спецификации»: загрузить подписанную спецификацию (PDF). */
  uploadSignedSpecification(bargainId: number, file: File): Promise<void> {
    return this.uploadSignedDocument(bargainId, 'SIGNED_SPECIFICATION', file);
  }

  /** Шаг «Поручение экспедитору»: агент загружает поручение, подписанное вне ЭДО. */
  uploadSignedForwarderAssignment(bargainId: number, file: File): Promise<void> {
    return this.uploadSignedDocument(bargainId, 'SIGNED_FORWARDER_ASSIGNMENT', file);
  }

  /** Шаг «Перевозка» → «Подпись УПД»: загрузить подписанный дневной УПД (симуляция ЭДО на dev). */
  uploadSignedDailyUpd(bargainId: number, file: File): Promise<void> {
    return this.uploadSignedDocument(bargainId, 'SIGNED_DAILY_UPD', file);
  }
}

export const dealService = new DealService();
